import { Avatar, Box, Card, ThemeProvider, Typography, Button, Divider, Dialog } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import type { TypedUseSelectorHook } from "react-redux";
import type { AppDispatch, RootState } from "../store";
import { useMemo, useState, useEffect, memo, useCallback } from "react";
import Theme from "../utils/theme";
import { useNavigate, useParams } from "react-router-dom";
import PostCardFeed from "../components/PostCardFeed";
import ImageCardFeed from "../components/ImageCardFeed";
import ImageUpload from "../components/ImageUpload";
import axiosService from "../utils/axios";
import axios from "axios";
import { setAccount } from "../store/slices/auth";
import type { IUser } from "../types/auth";
import CustomButton from "../components/default/CustomButton";
import FollowTab from "../components/FollowTab";
import type { IFollowUser, IFollowersData } from "../types/socialEdges";
import { format } from "date-fns";
import { resetPosts } from "../store/slices/posts";
import LoadingWithTip from "../components/LoadingWithTip";
import NotFound from "../components/NotFound";
import { scrollbarCss } from "../utils/themeScrollbar";

// *** Memoized Components *** //
const MemoizedPostCardFeed = memo(PostCardFeed);
const MemoizedImageCardFeed = memo(ImageCardFeed);

// *** Hooks *** //
const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;

// ***** Main Component ***** //
const Profile: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const authState = useTypedSelector((state) => state.auth);
    const themeState = useTypedSelector((state) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);

    const { username: routeUsername } = useParams();

    // Redirect for routerUsernames that do not start with @ //
    const needsRedirect = !!routeUsername && !routeUsername.startsWith("@");

    useEffect(() => {
        if (needsRedirect) {
            navigate(`/profile/@${routeUsername}`, { replace: true });
        }
    }, [needsRedirect, routeUsername, navigate]);

    const account = authState.account;
    const hasAccount = !!account;
    const accountUsername = account?.username ?? null;
    const targetUsername = routeUsername ?? accountUsername ?? null;

    // *** Component States *** //
    const [profileOwner, setProfileOwner] = useState(false);
    const [isGuest, setIsGuest] = useState(true);
    const [userProfile, setUserProfile] = useState<IUser | null>(null);
    const [feedLoading, setFeedLoading] = useState(false);
    const [profileState, setProfileState] = useState<"Posts" | "Images" | "Followers" | "Followings">("Posts");
    const [openAvatarModal, setOpenAvatarModal] = useState(false);
    const [loading, setLoading] = useState(true);
    const [followButtonText, setFollowButtonText] = useState("Follow");
    const [followers, setFollowers] = useState<IFollowersData | null>(null);
    const [followings, setFollowings] = useState<IFollowersData | null>(null);

    // *** Effects (each guarded by needsRedirect) *** //
    useEffect(() => {
        return () => {
            dispatch(resetPosts());
            setFollowers(null);
            setFollowings(null);
        };
    }, [dispatch]);

    useEffect(() => {
        if (needsRedirect) return;
        setIsGuest(!hasAccount);
    }, [hasAccount, needsRedirect]);

    useEffect(() => {
        if (needsRedirect) return;
        if (!routeUsername) {
            // profile without explicit route param -> own profile if logged in //
            setProfileOwner(Boolean(accountUsername && !routeUsername));
        } else {
            setProfileOwner(routeUsername.replace(/^@/, "") === accountUsername);
        }
    }, [routeUsername, accountUsername, needsRedirect]);

    const fetchProfile = useCallback((username: string) => {
        setLoading(true);
        axios
            .get(`${process.env.REACT_APP_API_URL}/users/fetchByUsername`, { params: { username: username } })
            .then((res) => setUserProfile(res.data.body))
            .catch((error) => console.error("Failed to fetch user profile. ", error))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (needsRedirect) return;
        if (!targetUsername) return;
        fetchProfile(targetUsername);
    }, [targetUsername, fetchProfile, needsRedirect]);

    useEffect(() => {
        if (
            needsRedirect ||
            isGuest ||
            !targetUsername ||
            (accountUsername && targetUsername === accountUsername) ||
            followButtonText !== "Follow"
        )
            return;

        let active = true;

        axiosService
            .get(`${process.env.REACT_APP_API_URL}/socials/follower/status`, {
                params: { srcUsername: accountUsername, destUsername: targetUsername },
            })
            .then((res) => {
                if (!active) return;
                switch (res.data.body) {
                    case "pending":
                        setFollowButtonText("Pending");
                        break;
                    case "approved":
                        setFollowButtonText("Followed");
                        break;
                    default:
                        break;
                }
            })
            .catch(() => {});

        return () => {
            active = false;
        };
    }, [targetUsername, accountUsername, followButtonText, isGuest, needsRedirect]);

    const updateFollowers = useCallback(async () => {
        if (needsRedirect || !targetUsername) return;

        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/socials/follower`, {
                params: { destUsername: targetUsername },
            });

            const list = res.data.body ?? [];
            const transformed = list.map((item: any) => ({
                _id: item._id,
                username: item.srcId.username,
                image: item.image,
                status: item.status,
                createdAt: format(new Date(item.createdAt), "MMM dd, yyyy HH:mm"),
                updatedAt: format(new Date(item.updatedAt), "MMM dd, yyyy HH:mm"),
            }));

            setFollowers({
                pending: transformed.filter((f: IFollowUser) => f.status === "pending"),
                approved: transformed.filter((f: IFollowUser) => f.status === "approved"),
            });
        } catch {
            setFollowers({ pending: [], approved: [] });
        }
    }, [needsRedirect, targetUsername]);

    const updateFollowings = useCallback(async () => {
        if (needsRedirect || !targetUsername) return;

        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL}/socials/following`, { params: { srcUsername: targetUsername } });

            const list = res.data.body ?? [];
            const transformed = list.map((item: any) => ({
                _id: item._id,
                username: item.destId.username,
                image: item.image,
                status: item.status,
                createdAt: format(new Date(item.createdAt), "MMM dd, yyyy HH:mm"),
                updatedAt: format(new Date(item.updatedAt), "MMM dd, yyyy HH:mm"),
            }));

            setFollowings({
                pending: transformed.filter((f: IFollowUser) => f.status === "pending"),
                approved: transformed.filter((f: IFollowUser) => f.status === "approved"),
            });
        } catch {
            setFollowings({ pending: [], approved: [] });
        }
    }, [needsRedirect, targetUsername]);

    useEffect(() => {
        if (needsRedirect) return;
        setFollowers(null);
        setFollowings(null);
    }, [needsRedirect, targetUsername]);

    useEffect(() => {
        if (needsRedirect) return;
        if (followers !== null) return;
        updateFollowers();
    }, [updateFollowers, followers, needsRedirect]);

    useEffect(() => {
        if (needsRedirect) return;
        if (followings !== null) return;
        updateFollowings();
    }, [needsRedirect, followings, updateFollowings]);

    const requestFollow = useCallback(() => {
        if (needsRedirect || isGuest || profileOwner || !targetUsername) return;

        axiosService
            .post(`${process.env.REACT_APP_API_URL}/socials/follower/request`, {
                srcUsername: accountUsername,
                destUsername: targetUsername,
            })
            .then(() => setFollowButtonText("Pending"));
    }, [needsRedirect, isGuest, profileOwner, targetUsername, accountUsername]);

    const handleAvatarClick = useCallback(() => {
        if (needsRedirect || !profileOwner) return;
        setOpenAvatarModal(true);
    }, [profileOwner, needsRedirect]);

    const uploadProfileAvatar = useCallback(
        (previewUrl: string | null, data: { file: File | null }) => {
            if (needsRedirect || !data.file || isGuest) return;

            const formData = new FormData();
            formData.append("image", data.file);

            axiosService
                .post(`${process.env.REACT_APP_API_URL}/users/image/upload`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                })
                .then((res) => {
                    setUserProfile(res.data.body);
                    setOpenAvatarModal(false);
                    dispatch(setAccount(res.data.body));
                })
                .catch((error) => {
                    setOpenAvatarModal(false);
                    console.error("Failed upload avatar: ", error);
                });
        },
        [dispatch, isGuest, needsRedirect]
    );

    if (needsRedirect) return null;

    if (loading)
        return (
            <Box
                sx={{
                    height: "calc(100dvh - 200px)",
                    display: "flex",
                    width: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <LoadingWithTip />
            </Box>
        );

    if (!userProfile) return <NotFound />;

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        maxWidth: 1200,
                    }}
                >
                    <Card
                        sx={{
                            m: { xs: 0, sm: 2 },
                            height: 250,
                            position: "relative",
                            borderRadius: { xs: 0, sm: 10 },
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                top: { xs: 10, sm: 90 },
                                left: { xs: "calc(50% - 75px)", sm: 10 },
                                height: 150,
                                width: 150,
                                zIndex: 1,
                                borderRadius: "50%",
                                overflow: "hidden",
                                boxShadow: "0 0 0 1.5px black",
                                ...(profileOwner && {
                                    cursor: "pointer",
                                    "&:hover .overlay": { opacity: 1 },
                                }),
                            }}
                            onClick={profileOwner ? handleAvatarClick : undefined}
                        >
                            <Avatar src={userProfile.image ? userProfile.image : ""} sx={{ width: "100%", height: "100%" }} />
                            {profileOwner && (
                                <Box
                                    className="overlay"
                                    sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        height: "100%",
                                        bgcolor: "rgba(0, 0, 0, 0.4)",
                                        color: "white",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        fontSize: "0.95rem",
                                        fontWeight: "bold",
                                        opacity: 0,
                                        transition: "opacity 0.3s ease",
                                        textAlign: "center",
                                    }}
                                >
                                    Upload new avatar
                                </Box>
                            )}
                        </Box>

                        <Box
                            sx={{
                                position: "relative",
                                height: 200,
                                width: "100%",
                                backgroundImage: "url('/images/accountBackground.png')",
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    width: { xs: "100vw", sm: "auto" },
                                    textAlign: "center",
                                    position: "absolute",
                                    bottom: { xs: 5, sm: 10 },
                                    left: { sm: 175 },
                                    color: "white",
                                    textShadow: `
                    -1px -1px 0 black,
                    1px -1px 0 black,
                    -1px  1px 0 black,
                    1px  1px 0 black
                  `,
                                }}
                            >
                                {userProfile.user}
                            </Typography>

                            {!isGuest && !profileOwner && (
                                <CustomButton
                                    onClick={requestFollow}
                                    label={followButtonText}
                                    sx={{ position: "absolute", right: 15, top: 15, backgroundColor: "white" }}
                                    disabled={followButtonText === "Pending" || followButtonText === "Followed"}
                                />
                            )}
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                width: "100%",
                                height: 50,
                                pl: { xs: 0, sm: 20 },
                                justifyContent: { xs: "center", sm: "normal" },
                            }}
                        >
                            <Divider orientation="vertical" variant="middle" flexItem />
                            <Button variant="text" onClick={() => setProfileState("Posts")}>
                                Posts
                            </Button>
                            <Divider orientation="vertical" variant="middle" flexItem />
                            <Button variant="text" onClick={() => setProfileState("Images")}>
                                Images
                            </Button>
                            <Divider orientation="vertical" variant="middle" flexItem />
                            <Button variant="text" onClick={() => setProfileState("Followers")}>
                                Followers
                            </Button>
                            <Divider orientation="vertical" variant="middle" flexItem />
                            {profileOwner && (
                                <>
                                    <Button variant="text" onClick={() => setProfileState("Followings")}>
                                        Following
                                    </Button>
                                    <Divider orientation="vertical" variant="middle" flexItem />
                                </>
                            )}
                        </Box>
                    </Card>

                    <Box sx={{ flexGrow: 1, mx: { xs: 0, sm: 2 }, px: { xs: 0, sm: 2 }, pb: 5 }}>
                        {profileState === "Posts" && (
                            <MemoizedPostCardFeed
                                feedLoading={feedLoading}
                                feedSize={""}
                                username={targetUsername ?? undefined}
                                setFeedLoading={setFeedLoading}
                            />
                        )}
                        {profileState === "Images" && (
                            <Box
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    overflow: "auto",
                                    ...scrollbarCss,
                                }}
                            >
                                <MemoizedImageCardFeed type="username" username={userProfile.user} />
                            </Box>
                        )}
                        {profileState === "Followers" && followers !== null && (
                            <FollowTab
                                setFunction={setFollowers}
                                update={updateFollowers}
                                isProfileOwner={profileOwner}
                                data={followers}
                                tabType="Followers"
                            />
                        )}
                        {profileState === "Followings" && followings !== null && (
                            <FollowTab
                                setFunction={setFollowings}
                                update={updateFollowings}
                                isProfileOwner={profileOwner}
                                data={followings}
                                tabType="Followings"
                            />
                        )}
                    </Box>
                </Box>
            </Box>

            {openAvatarModal && profileOwner && (
                <Dialog open={openAvatarModal} onClose={() => setOpenAvatarModal(false)}>
                    <Box sx={{ pt: 1, px: 2, pb: 2 }}>
                        <ImageUpload type="Avatar" handleImageAdd={uploadProfileAvatar} />
                    </Box>
                </Dialog>
            )}
        </ThemeProvider>
    );
};

export default Profile;
